import { createContext, useContext, useState, useCallback } from 'react';
import { DEFAULT_SCORING } from '../data/scoring';
import { capturePlayerCardOpened } from '../utils/playerCardAnalytics';

const PlayerCardContext = createContext(null);

// A stable identity for "no stars registered", so a provider that never gets a
// star sheet does not hand its consumers a fresh {} on every render.
const EMPTY_STARS = {};

export function PlayerCardProvider({ children }) {
  const [playerNames, setPlayerNames] = useState([]);
  const [cardData, setCardData] = useState({});
  const [scoring, setScoring] = useState(DEFAULT_SCORING);
  // League-dynamic price map + budget, registered by AuctionDraftBuilder so the
  // shared card portal shows the SAME $ as Team Builder (keyed `${name}-${pos}`).
  const [dynamicPrices, setDynamicPrices] = useState(null);
  const [budget, setBudget] = useState(200);
  const [activeLeagueName, setActiveLeagueName] = useState(null);

  /**
   * "My guys" — the star sheet for the ACTIVE league, plus the toggle that
   * writes it. Registered by AuctionDraftBuilder exactly like dynamicPrices
   * above, and for the same reason: this provider sits above the Draft Plan
   * tree, so it cannot call usePlanState and usePlanState must not move.
   *
   * 🔴 THE TOGGLE IS BOXED IN AN OBJECT, and the box lives here so no call
   * site has to know about it. useState treats a bare function argument as a
   * lazy updater, so `setStarToggle(fn)` would CALL fn with the previous state
   * and store the result — a silent, confusing failure. Callers pass the plain
   * function; the wrapping happens below.
   *
   * Boxed rather than kept in a ref because `canStar` has to be REACTIVE: a
   * surface with no plan state behind it (a test harness, any future standalone
   * mount) must render no toggle at all rather than a dead one, and a ref write
   * cannot re-render to say so.
   */
  const [stars, setStars] = useState(EMPTY_STARS);
  const [starToggle, setStarToggleBox] = useState(null);
  const setStarToggle = useCallback((fn) => setStarToggleBox(fn ? { fn } : null), []);
  const toggleStar = useCallback((key) => { if (starToggle && key) starToggle.fn(key); }, [starToggle]);
  const isStarred = useCallback((key) => !!(key && stars[key]), [stars]);
  const canStar = !!starToggle;

  // `source` names the surface the open came from. Instrumented here rather
  // than at each button so no future call site can silently go untracked.
  const openPlayerCard = useCallback((name, data = {}, source) => {
    setPlayerNames([name]);
    setCardData(data);
    capturePlayerCardOpened(source);
  }, []);

  const addComparePlayer = useCallback((name) => {
    setPlayerNames(prev => {
      if (prev.includes(name) || prev.length >= 3) return prev;
      return [...prev, name];
    });
  }, []);

  const closePlayerCard = useCallback(() => {
    setPlayerNames([]);
    setCardData({});
  }, []);

  const removePlayer = useCallback((name) => {
    setPlayerNames(prev => prev.filter(n => n !== name));
  }, []);

  const playerName = playerNames.length > 0 ? playerNames[0] : null;
  const isCompareMode = playerNames.length > 1;

  return (
    <PlayerCardContext.Provider value={{
      playerName, playerNames, cardData,
      openPlayerCard, closePlayerCard, addComparePlayer, removePlayer,
      isCompareMode, scoring, setScoring,
      dynamicPrices, setDynamicPrices, budget, setBudget,
      activeLeagueName, setActiveLeagueName,
      stars, setStars, setStarToggle, toggleStar, isStarred, canStar,
    }}>
      {children}
    </PlayerCardContext.Provider>
  );
}

export function usePlayerCard() {
  const ctx = useContext(PlayerCardContext);
  if (!ctx) throw new Error('usePlayerCard must be used within PlayerCardProvider');
  return ctx;
}
